import { oc } from "@orpc/contract";
import * as z from "zod";
import { McpServerConfigInput, McpServerHealthSchema } from "../domain.js";
import { McpHeadersSchema, McpStdioFilesSchema, McpTransportSchema } from "../mcp.js";
import { DrokMcpAuthModeSchema, DrokToolGroupNameSchema, DrokToolkitSchema } from "./templates.js";

/** Only credential values may change; the template owns the server and its tool permissions. */
export const DrokCopyMcpCredentialsSchema = z.strictObject({
  serverId: z.string().min(1).max(200),
  secret: z.string().max(16384).optional(),
  headers: McpHeadersSchema.optional(),
  env: McpServerConfigInput.options[2].shape.env.optional(),
  files: McpStdioFilesSchema.optional(),
  oauthClient: z.union([
    McpServerConfigInput.options[2].shape.oauthClient,
    McpServerConfigInput.options[0].shape.oauthClient,
  ]),
  clearCredential: z.boolean().optional(),
});
export type DrokCopyMcpCredentials = z.infer<typeof DrokCopyMcpCredentialsSchema>;

/** This intentionally omits endpoints, source identities, secret references and credential values. */
export const DrokCopyMcpConnectionSchema = z.object({
  botId: z.string(),
  serverId: z.string(),
  name: z.string(),
  authMode: DrokMcpAuthModeSchema,
  transport: McpTransportSchema,
  envKeys: z.array(z.string()),
  headerKeys: z.array(z.string()),
  fileKeys: z.array(z.string()),
  hasSecret: z.boolean(),
  health: McpServerHealthSchema,
});
export type DrokCopyMcpConnection = z.infer<typeof DrokCopyMcpConnectionSchema>;

/** One agent IT pushed to the caller, with the parts of its template the employee may see. */
export const DrokMyCopySchema = z.object({
  botId: z.string(),
  templateName: z.string(),
  version: z.number().int(),
  toolGroups: z.array(DrokToolGroupNameSchema),
  toolkits: z.array(DrokToolkitSchema),
  skills: z.array(z.object({ name: z.string(), description: z.string() })),
});
export type DrokMyCopy = z.infer<typeof DrokMyCopySchema>;

export const drokCopyContract = {
  /** The caller's own live copies. Never returns instructions or other template internals. */
  mine: oc.output(z.array(DrokMyCopySchema)),
  mcpConnections: oc.output(z.array(DrokCopyMcpConnectionSchema)),
  setMcpCredentials: oc
    .input(DrokCopyMcpCredentialsSchema)
    .output(z.object({ ok: z.literal(true) })),
};
